<template>
  <v-app-bar app color="white" elevation="0" class="header-bar">
    <v-toolbar-title class="text-h6 font-weight-medium page-title">
      {{ pageTitle }}
    </v-toolbar-title>

    <v-spacer></v-spacer>

    <!-- Search -->
    <v-text-field
      density="compact"
      variant="outlined"
      placeholder="Search..."
      prepend-inner-icon="mdi-magnify"
      hide-details
      class="search-field mr-4"
      style="max-width: 300px;"
    ></v-text-field>

    <!-- Notifications -->
    <NotificationPanel />

    <!-- User Menu -->
    <v-menu offset-y>
      <template v-slot:activator="{ props }">
        <v-btn variant="text" v-bind="props" class="user-menu-btn">
          <v-avatar size="36" color="primary">
            <span class="text-white">{{ getUserInitials }}</span>
          </v-avatar>
          <span class="ml-2 text-body-2 font-weight-medium">{{ user?.full_name }}</span>
          <v-icon class="ml-1">mdi-chevron-down</v-icon>
        </v-btn>
      </template>
      <v-list>
        <v-list-item class="user-info-item">
          <v-list-item-title class="font-weight-medium">{{ user?.full_name }}</v-list-item-title>
          <v-list-item-subtitle class="text-caption">{{ user?.email }}</v-list-item-subtitle>
        </v-list-item>
        <v-divider class="my-1"></v-divider>
        <v-list-item @click="goToCompany" prepend-icon="mdi-domain">
          <v-list-item-title>Company</v-list-item-title>
        </v-list-item>
        <v-list-item v-if="isCompanyAdmin" @click="goToUsers" prepend-icon="mdi-account-multiple">
          <v-list-item-title>Users</v-list-item-title>
        </v-list-item>
        <v-list-item @click="goToProfile" prepend-icon="mdi-account-outline">
          <v-list-item-title>Profile</v-list-item-title>
        </v-list-item>
        <v-list-item v-if="isCompanyAdmin" @click="goToSettings" prepend-icon="mdi-cog-outline">
          <v-list-item-title>Settings</v-list-item-title>
        </v-list-item>        
        <v-divider class="my-1"></v-divider>
        <v-list-item @click="handleLogout" prepend-icon="mdi-logout">
          <v-list-item-title>Logout</v-list-item-title>
        </v-list-item>
      </v-list>
    </v-menu>
  </v-app-bar>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useNotificationStore } from '@/stores/notification'
import NotificationPanel from './NotificationPanel.vue'

const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()
const notificationStore = useNotificationStore()

const user = computed(() => authStore.user)
const isCompanyAdmin = computed(() => user.value?.role === 'admin')

const getUserInitials = computed(() => {
  if (!user.value?.full_name) return 'U'
  const names = user.value.full_name.split(' ')
  if (names.length >= 2) {
    return (names[0][0] + names[1][0]).toUpperCase()
  }
  return user.value.full_name.substring(0, 2).toUpperCase()
})

const pageTitle = computed(() => {
  const routeNames: Record<string, string> = {
    'Dashboard': 'Dashboard',
    'Farms': 'Farm Management',
    'FarmDetail': 'Farm Details',
    'Ponds': 'Pond Management',
    'PondDetail': 'Pond Details',
    'FishInventory': 'Fish Inventory',
    'BatchDetail': 'Batch Details',
    'WaterQuality': 'Water Quality',
    'Feeding': 'Feeding Management',
    'FeedInventory': 'Feed Inventory',
    'Financial': 'Financial Management',
    'Reports': 'Reports & Analytics',
    'Settings': 'Settings',
    'Profile': 'My Profile'
  }
  return routeNames[route.name as string] || 'AquaFarm Pro'
})

function goToCompany() {
  router.push('/company')
}

function goToProfile() {
  router.push('/profile')
}

function goToSettings() {
  router.push('/settings')
}

function goToUsers() {
  router.push('/users')
}

async function handleLogout() {
  try {
    await authStore.logout()
    notificationStore.showSuccess('Logged out successfully')
    router.push('/login')
  } catch (error) {
    notificationStore.showError('Logout failed')
  }
}
</script>

<style scoped>
.v-app-bar {
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}
</style>header-bar {
  border-bottom: 1px solid #e0e0e0 !important;
}

.page-title {
  color: #263238;
}

.search-field {
  border-radius: 24px;
}

.user-menu-btn {
  text-transform: none;
  height: 48px !important;
  border-radius: 24px;
}

.user-info-item {
  padding: 12px 16px;
  background-color: #f5f5f5